import { AddIcon, ArrowForwardIcon, MinusIcon } from '@chakra-ui/icons'
import { Accordion, AccordionButton, AccordionItem, AccordionPanel, Box, Button, Flex, Text, Image as ChakraImage } from '@chakra-ui/react'
import Head from 'next/head'
import Image from "next/legacy/image"
import Link from 'next/link'
import React from 'react'
import { AiOutlineFileDone } from 'react-icons/ai'
import { BsFillChatLeftTextFill } from 'react-icons/bs'
import { IoIosRocket, IoMdPhotos } from 'react-icons/io'
import { ProfessionalsCard } from '../../components/Landing'
import * as S from '../../styles/SellingStyles'

function Selling() {

    const sellerTypes = [
        {
            id: `${String(Math.random()).replace(".", "_")}`,
            img: "/images/newLanding/realtorsImg.png",
            route: "/professionals",
            title: "Professionals",
            body: "Are you a Realtor or another professional who routinely lists properties? You’ll love Zonado."
        },
        {
            id: `${String(Math.random()).replace(".", "_")}`,
            img: "/images/newLanding/businessOwnersImg.png",
            route: "/owners",
            title: "Property & Business Owners",
            body: "Are you looking to sell the business or property that you own? You’ve come to the right place.",
        },
    ]

    const tips = [
        {
            icon:
                <IoMdPhotos />,
            header: 'Add clear photos and description',
            description: 'Use high resolution photos to clearly show what you’re listing. Fill out as many fields as possible while creating your listing - richer listings perform better on Zonado.'
        },
        {
            icon: <BsFillChatLeftTextFill />,
            header: 'Respond quickly to Messages',
            description: 'If you’d like to attract more prospective buyers, get better offers and sell your property or business faster, keep the following best practices in mind when creating your listings for Zonado.'
        },
        {
            icon: 
                <IoIosRocket />,
            header: 'Boost your listing',
            description: 'You can boost your listing so that it reaches more people on Zonado and beyond. Message hello@zonado.com to boost your listing.'
        },
        {
            icon: 
                <AiOutlineFileDone />,
            header: 'Mark your listing as sold',
            description: 'When you have successfully sold your listing, mark it as sold so you don’t receive additional offers.'
        }
    ]

    const faqs = [
        {
            question: 'What is Zonado?',
            answer: 'Zonado is an online marketplace to allow owners, investors, agents, and everyone else involved in the sale of commercial property and businesses to come together in a seamless fashion. We are a small team of people spread out across Canada who care deeply about small businesses, and about enabling people to find the right property for them.'
        },
        {
            question: 'Is Zonado a brokerage?',
            answer: 'Zonado is not a brokerage. We are not affiliated with any brokerage and we are not real estate agents or business brokers. We are independent and are here to help you succeed in selling your own or your client’s business or property.'
        },
        {
            question: 'What can be sold on Zonado?',
            answer: 'Businesses (with or without property), commercial real estate (land, retail, industrial, multifamily, office, etc.) can be listed on Zonado. In order to advertise a listing on Zonado, you must own the asset or have an agreement with the owner of the asset to advertise on their behalf. '
        },
        {
            question: 'How do I sell a property or business on Zonado?',
            answer: `When you sell a property or business on Zonado, you create a public listing that can be seen by anyone on and off Zonado. \n 
            As you create your listing, keep in mind that there are dozens of property types/subtypes and business types to choose from when you're creating a listing on Zonado. Categorizing your item will help ensure people can more easily find it.
            `
        },
        {
            question: 'Do I have to be an agent to advertise on Zonado?',
            answer: 'Zonado welcomes both agents and owners. You do not have to be an agent to advertise on Zonado.'
        },
        {
            question: 'Is my information sold to third parties?',
            answer: 'Zonado is a secure service and your information is strictly your own. We never have and never will engage in selling data to third parties.'
        },
        {
            question: 'Do my leads come directly to me?',
            answer: 'Yes. All leads for your listing will always come directly to you.'
        }
    ]

    function SellerTypeCard({
        img,
        route,
        body,
        title
    }) {
        return (
            (<Box
                height='100%'
                width="100%"
                margin='0px'
                bg={"#F7F8FA"}
            >
                <Link href={`/selling${route}`}>

                    <Flex
                    shadow={"0"}
                    height={{ base: "100%" }}
                    flexDir={"column"}
                    justifyContent={"space-between"}
                    alignItems={"flex-start"}
                    >
                    <Box>
                        <Text
                        fontSize={{ base: "25px" }}
                        fontWeight={"500"}
                        fontStyle={"medium"}
                        lineHeight={{ base: "36px" }}
                        >
                        {title}
                        </Text>
                        <Text
                        color={"gray.500"}
                        mt={"10px"}
                        mb={"18px"}
                        fontSize={'16px'}
                        fontWeight={"500"}
                        lineHeight={'1.5'}
                        >
                        {body}
                        </Text>
                    </Box>
        
                    <Box w="100%">
                        <Button
                            variant={"link"}
                            color={"brand.800"}
                            mb={"14px"}
                            outline={"0"}
                            fontWeight="500"
                        >
                            Learn More
                            <ArrowForwardIcon w={"20px"} h={"20px"} ml={"2"} />
                        </Button>
        
                        <Box w={'100%'}>
                            <ChakraImage
                                src={img}
                                width={"100%"}
                                height={"257px"}
                                alt={`for ${title}`}
                            />
                        </Box>
                    </Box>
                    </Flex>

                </Link>
            </Box>)
        );
    }

  return (<>
      <Head>
          <title key="title">
              Selling on Zonado
          </title>
          <meta
              key="description"
              name="description"
              content="Zonado makes it easier to sell commercial real estate and businesses in Canada. Here's more information about selling on Zonado. "
          />
          <meta
              key="og:title"
              property="og:title"
              content="Selling on Zonado"
          />
          <meta
              key="og:description"
              property="og:description"
              content="Zonado makes it easier to sell commercial real estate and businesses in Canada. Here's more information about selling on Zonado. "
          />
      </Head>
      <S.SellingPageContainer>
          <S.Header>
              <Flex
                  bgColor="#FFFFFF"
                  width={76}
                  height={76}
                  borderRadius="50%"
                  justifyContent="center"
                  alignItems="center"
              >
                  <Image
                      src="/images/selling/banner-icon.png"
                      width={35}
                      height={35}
                      style={{
                          maxWidth: "100%",
                          height: "auto"
                      }} />
              </Flex>
              <S.HeaderText>Selling on Zonado</S.HeaderText>
              <S.HeaderDesc>
              Are you interested in selling on Zonado? Zonado makes it easier to sell commercial real estate and businesses in Canada. Here's more information about selling on Zonado. 
              </S.HeaderDesc>
              <Link href="/add-listing">

                  <S.CTAButton>Create a free listing</S.CTAButton>

              </Link>
          </S.Header>
          <S.TypeOfSellerSection>
              <S.SectionTitle>What kind of seller are you?</S.SectionTitle>
              <S.SellerTypeCardWrapper>
              {
                  sellerTypes.map(({ id, ...rest }) => (
                      <SellerTypeCard
                          key={id} 
                          {...rest}
                      />
                  ))
              }
              </S.SellerTypeCardWrapper>
          </S.TypeOfSellerSection>

          <S.TipsForSellingSection>
              <S.SectionTitle>
                  Tips for selling on Zonado
              </S.SectionTitle>
              <S.SectionDescription>
              If you’d like to attract more prospective buyers, get better offers and sell your property or business faster, keep the following best practices in mind when creating your listings for Zonado.
              </S.SectionDescription>

              <S.Tips>
                  {
                      tips.map(tip => (
                          <S.TipContainer>
                              <Flex
                                  flexDir="column"
                                  rowGap="15px"
                              >
                                  <S.TipIcon>
                                      {tip.icon}
                                  </S.TipIcon>
                                  <S.TipHeader>{tip.header}</S.TipHeader>
                              </Flex>
                              <Flex
                                  flexDir="column"
                                  rowGap="15px"
                              >
                                  <S.TipDesc>
                                  {tip.description}
                                  </S.TipDesc>
                              </Flex>
                          </S.TipContainer>
                      ))
                  }
              </S.Tips>
          </S.TipsForSellingSection>
          
          <S.FAQSection>
              <S.SectionTitle>FAQs</S.SectionTitle>
              <Accordion allowMultiple>
                  {
                      faqs.map(({ question, answer }) => (
                          <AccordionItem
                              borderTop="none"
                              borderBottom="1px solid #E2E8F0"
                          >
                              {({ isExpanded }) => (
                              <>
                                  <AccordionButton
                                      display="flex"
                                      flexDir="row"
                                      justifyContent={'space-between'}
                                      alignItems="center"
                                      p="20px"
                                      columnGap="20px"
                                  >
                                      <S.FAQHeaderText>
                                          {question}
                                      </S.FAQHeaderText>
                                      <Box
                                          display="flex"
                                          justifyContent={'center'}
                                          alignItems={'center'}
                                          width={30}
                                          height={30}
                                          flex="0 0 30px"
                                          bgColor={"#125F3F"}
                                          color="#fff"
                                          borderRadius="50%"
                                      >
                                          {isExpanded ? (
                                          <MinusIcon fontSize='12px' />
                                          ) : (
                                          <AddIcon fontSize='12px' />
                                          )}
                                      </Box>
                                  </AccordionButton>
                                  <AccordionPanel pb={4}>
                                      {answer}
                                  </AccordionPanel>
                              </>
                              )}
                          </AccordionItem>
                      ))
                  }
              </Accordion>
          </S.FAQSection>

          <S.Footer>
              <Flex
                  bgColor="#FFFFFF"
                  width={76}
                  height={76}
                  borderRadius="50%"
                  justifyContent="center"
                  alignItems="center"
              >
                  <Image
                      src="/images/selling/banner-icon.png"
                      width={35}
                      height={35}
                      style={{
                          maxWidth: "100%",
                          height: "auto"
                      }} />
              </Flex>
              <S.FooterText>Sell on Zonado</S.FooterText>
              <Link href="/add-listing">

                  <S.CTAButton>Create a free listing</S.CTAButton>

              </Link>
          </S.Footer>
      </S.SellingPageContainer>
  </>);
}

Selling.newLayout = true

export default Selling
